import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import {
  ReviewTimeDropdown,
  InternalReviewDropdown,
  TeamMemberDropdown,
  ProjectItemDropdown,
  DurationDropdown,
  DateDropdown,
} from "@/components/submittal/column-dropdowns";
import { cn } from "@/lib/utils";

export interface OrgMemberOption {
  _id: string;
  name: string;
  email: string;
}

export interface NamedCustomItem {
  _id: string;
  name: string;
  type?: string;
}

export interface TemplateConfigColumn {
  key: string;
  header: string;
  type: "team_member" | "project_item" | "duration" | "date";
  columnIndex: number;
  defaultValue?: string;
}

export type CreateCustomItem = (args: {
  projectId: Id<"projects">;
  type: string;
  name: string;
}) => Promise<unknown>;

function projectItemTypeForColumn(columnKey: string) {
  if (columnKey === "location") return "location";
  if (columnKey.includes("contractor")) return "contractor";
  return `project_item:${columnKey}`;
}

export function ConfigApplyHint({ selectedRowCount }: { selectedRowCount: number }) {
  return (
    <p className="text-xs text-muted-foreground">
      {selectedRowCount > 0
        ? `Will apply to ${selectedRowCount} selected row${selectedRowCount !== 1 ? "s" : ""}`
        : "No rows selected - will apply to all rows"}
    </p>
  );
}

function ConfigField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function DynamicConfigControl({
  column,
  value,
  orgMembers,
  projectCustomItems,
  projectId,
  createCustomItem,
  reviewTimesConfirmed,
  onConfigColumnChange,
}: {
  column: TemplateConfigColumn;
  value: string;
  orgMembers: OrgMemberOption[];
  projectCustomItems: NamedCustomItem[];
  projectId?: Id<"projects">;
  createCustomItem?: CreateCustomItem;
  reviewTimesConfirmed: boolean;
  onConfigColumnChange?: (column: TemplateConfigColumn, value: string) => void;
}) {
  const commonClassName = cn(
    "h-8 text-sm w-full",
    column.type === "duration" && !reviewTimesConfirmed && "border-status-pending/40"
  );

  if (column.type === "team_member") {
    return (
      <ConfigField label={column.header}>
        <TeamMemberDropdown
          members={orgMembers}
          value={value}
          onChange={(next) => onConfigColumnChange?.(column, next)}
          placeholder={column.header}
          className={commonClassName}
        />
      </ConfigField>
    );
  }

  if (column.type === "project_item" && projectId && createCustomItem) {
    const itemType = projectItemTypeForColumn(column.key);
    return (
      <ConfigField label={column.header}>
        <ProjectItemDropdown
          items={projectCustomItems.filter((item) => item.type === itemType)}
          itemLabel={column.header}
          value={value}
          onChange={(next) => onConfigColumnChange?.(column, next)}
          onAddNew={async (name) => {
            await createCustomItem({ projectId, type: itemType, name });
          }}
          placeholder={column.header}
          className={commonClassName}
        />
      </ConfigField>
    );
  }

  if (column.type === "date") {
    return (
      <ConfigField label={column.header}>
        <DateDropdown
          value={value}
          onChange={(next) => onConfigColumnChange?.(column, next)}
          className={commonClassName}
        />
      </ConfigField>
    );
  }

  const durationItems = projectCustomItems.filter((item) => item.type === "duration");

  return (
    <ConfigField label={column.header}>
      <DurationDropdown
        value={value}
        onChange={(next) => onConfigColumnChange?.(column, next)}
        className={commonClassName}
        items={durationItems}
        projectId={projectId}
        onAddNew={projectId && createCustomItem ? async (name) => {
          await createCustomItem({ projectId, type: "duration", name });
        } : undefined}
      />
    </ConfigField>
  );
}

export function DynamicConfigurationControls({
  templateConfigColumns,
  configValues,
  orgMembers,
  projectCustomItems,
  projectId,
  createCustomItem,
  reviewTimesConfirmed,
  onConfigColumnChange,
}: {
  templateConfigColumns: TemplateConfigColumn[];
  configValues: Record<string, string>;
  orgMembers: OrgMemberOption[];
  projectCustomItems: NamedCustomItem[];
  projectId?: Id<"projects">;
  createCustomItem?: CreateCustomItem;
  reviewTimesConfirmed: boolean;
  onConfigColumnChange?: (column: TemplateConfigColumn, value: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
      {templateConfigColumns.map((column) => (
        <DynamicConfigControl
          key={column.key}
          column={column}
          value={configValues[column.key] ?? column.defaultValue ?? ""}
          orgMembers={orgMembers}
          projectCustomItems={projectCustomItems}
          projectId={projectId}
          createCustomItem={createCustomItem}
          reviewTimesConfirmed={reviewTimesConfirmed}
          onConfigColumnChange={onConfigColumnChange}
        />
      ))}
    </div>
  );
}

export function LegacyConfigurationControls({
  orgMembers,
  submittalManager,
  designReviewTime,
  internalReviewTime,
  reviewTimesConfirmed,
  onSubmittalManagerChange,
  onDesignReviewTimeChange,
  onInternalReviewTimeChange,
}: {
  orgMembers: OrgMemberOption[];
  submittalManager: string | null;
  designReviewTime: string;
  internalReviewTime: string;
  reviewTimesConfirmed: boolean;
  onSubmittalManagerChange: (managerEmail: string) => void;
  onDesignReviewTimeChange: (value: string) => void;
  onInternalReviewTimeChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
      <ConfigField label="Manager">
        <TeamMemberDropdown
          members={orgMembers}
          value={submittalManager ?? undefined}
          onChange={onSubmittalManagerChange}
          className={cn(
            "h-8 text-sm w-full",
            submittalManager === null &&
              "border-status-pending/50 bg-status-pending/5 ring-2 ring-status-pending/20"
          )}
        />
      </ConfigField>
      <ConfigField label="Design Review">
        <ReviewTimeDropdown
          value={designReviewTime}
          onChange={onDesignReviewTimeChange}
          className={cn("h-8 text-sm w-full", !reviewTimesConfirmed && "border-status-pending/40")}
        />
      </ConfigField>
      <ConfigField label="Internal Review">
        <InternalReviewDropdown
          value={internalReviewTime}
          onChange={onInternalReviewTimeChange}
          className={cn("h-8 text-sm w-full", !reviewTimesConfirmed && "border-status-pending/40")}
        />
      </ConfigField>
    </div>
  );
}

function formatDays(value: string) {
  return `${value} day${value === "1" ? "" : "s"}`;
}

/** One-line summary of the current configuration, shown while the setup panel is collapsed. */
export function summarizeConfiguration({
  templateConfigColumns,
  configValues,
  submittalManager,
  designReviewTime,
  internalReviewTime,
}: {
  templateConfigColumns: TemplateConfigColumn[];
  configValues: Record<string, string>;
  submittalManager: string | null;
  designReviewTime: string;
  internalReviewTime: string;
}) {
  if (templateConfigColumns.length > 0) {
    return templateConfigColumns
      .map((column) => {
        const value = configValues[column.key] || column.defaultValue || "";
        if (!value) return `${column.header}: Not set`;
        return `${column.header}: ${column.type === "duration" ? formatDays(value) : value}`;
      })
      .join(" · ");
  }
  return [
    `Manager: ${submittalManager ?? "Not set"}`,
    `Design Review: ${formatDays(designReviewTime)}`,
    `Internal Review: ${formatDays(internalReviewTime)}`,
  ].join(" · ");
}
